import { _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { Activity } from '@/_types/models/activitites';
import type { TaxReturn, VoucherDefinition } from '@/_types/models/taxReturns';
import type {
  DeliverFilterObject,
  FormDefinition,
  DeliveryMessageTemplate,
  ClassificationPageSectionType,
} from '@/_types/taxReturnDelivery';

export interface TaxReturnsResponse {
  tax_returns: Array<TaxReturn>;
  meta: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_count: number;
  };
}

export const getAllTaxReturns = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend('/tax_returns/get_tax_returns_index', 'GET', null, token);

export const getPaginatedTaxReturns = async ({
  token,
  page = 1,
  per_page = 10,
  sort_by,
  sort_direction,
  filter,
}: {
  token: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  filter?: DeliverFilterObject;
}): Promise<TaxReturnsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
    ...(sort_by && { sort_by }),
    ...(sort_direction && { sort_direction }),
    ...(filter?.search_term && { search_term: filter.search_term }),
    ...(filter?.year && { year: filter.year.toString() }),
    ...(filter?.status?.length && { status: filter.status.join(',') }),
    ...(filter?.k1_distribution_status?.length && {
      k1_distribution_status: filter.k1_distribution_status.join(','),
    }),
    ...(filter?.reminder_disabled && {
      reminder_disabled: filter.reminder_disabled,
    }),
    ...(filter?.reminder_sent && { reminder_sent: filter.reminder_sent }),
    ...(filter?.voucher_payments_made && {
      voucher_payments_made: filter.voucher_payments_made,
    }),
  });

  return await _callBackend(
    `/tax_returns/get_paginated_tax_returns?${params}`,
    'GET',
    null,
    token,
  );
};

export const getVoucherDefinitions = async ({
  token,
}: APIRequest): APIResponsePromise<Array<VoucherDefinition>> =>
  await _callBackend(
    '/tax_returns/get_voucher_definitions',
    'GET',
    null,
    token,
  );

export const getFormDefinitions = async ({
  token,
}: APIRequest): APIResponsePromise<Array<FormDefinition>> =>
  await _callBackend(
    '/signature_requests/get_form_definitions',
    'GET',
    null,
    token,
  );

export const updateTaxReturn = async ({
  id,
  companyId,
  token,
  ...data
}: APIRequest<{
  id: number;
  companyId: number;
  status?: string;
  tax_return_name?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}`,
    'PATCH',
    {
      company_id: companyId,
      ...data,
    },
    token,
  );
};

export const deleteTaxReturn = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}`,
    'DELETE',
    {
      company_id: companyId,
    },
    token,
    { returnResponseAsIs: true },
  );
};

export const uploadTaxReturn = async ({
  companyId,
  documentName,
  files,
  path,
  taxYear,
  sendDraftForApproval,
  taxSummaryEnabled,
  previousTaxReturnId,
  token,
}: APIRequest<{
  companyId: number;
  documentName?: string;
  files: File[];
  path: string;
  taxYear: number;
  sendDraftForApproval: boolean;
  taxSummaryEnabled: boolean;
  previousTaxReturnId?: number;
}>): APIResponsePromise<void> => {
  if (files.length === 0) {
    throw new Error('No files provided for upload');
  }

  // Get upload links for all files
  const uploadLinks = await Promise.all(
    files.map(async () => {
      const { upload_link, id } = await _callBackend(
        `/integrations/get_temporary_upload_link?company_id=${companyId}`,
        'GET',
        null,
        token,
      );
      return { upload_link, id };
    }),
  );

  // Upload all files in parallel
  const fileUploadPromises = files.map(async (fileToUpload, index) => {
    const { upload_link, id } = uploadLinks[index];
    const url = new URL(upload_link);
    const isDropbox: boolean = url.hostname.includes('dropbox');

    // Intentionally not using `fileUpload` here so we can check the HTTP status
    // without changing the global `fileUpload` implementation.
    const resp = await fetch(upload_link, {
      method: isDropbox ? 'POST' : 'PUT',
      headers: isDropbox
        ? { 'Content-Type': 'application/octet-stream' }
        : { 'Content-Type': fileToUpload?.type, Accept: 'application/json' },
      body: fileToUpload,
    });

    if (!resp.ok) {
      const bodyText = await resp.text().catch(() => null);
      throw new Error(
        `Upload failed for ${fileToUpload.name}: ${resp.status} ${resp.statusText}` +
          (bodyText ? ` - ${bodyText}` : ''),
      );
    }

    return { id, fileName: fileToUpload.name };
  });

  const uploadResults = await Promise.all(fileUploadPromises);

  // Process uploaded documents
  const processResponse: Response = await _callBackend(
    '/tax_returns/process_documents',
    'POST',
    {
      file_ids: uploadResults.map((result) => result.id),
      document_name: documentName,
      company_id: companyId,
      path,
      previous_tax_return_id: previousTaxReturnId,
      tax_year: taxYear,
      send_draft_for_approval: sendDraftForApproval.toString(),
      tax_summary_enabled: taxSummaryEnabled,
    },
    token,
    { returnResponseAsIs: true }, // To ensure non-2xx responses are raised as errors
  );

  if (!processResponse.ok) {
    let body: any = null;
    try {
      body = await processResponse.json();
    } catch (e) {
      body = null;
    }

    const errMsg =
      body?.error ||
      `Upload processing failed: ${processResponse.status} ${processResponse.statusText}`;
    throw new Error(errMsg);
  }
};

export const updateTaxReturnIdentityVerification = async ({
  id,
  token,
  verificationId,
  phoneNumber,
  companyId,
}: APIRequest<{
  id: number;
  token?: string;
  verificationId: number;
  phoneNumber: string;
  companyId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}/update_identity_verification`,
    'POST',
    {
      verification_id: verificationId,
      phone_number: phoneNumber,
      company_id: companyId,
    },
    token,
  );
};

export const sendReminder = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  token?: string;
}>): APIResponsePromise<{
  message: string;
  error?: string;
}> => {
  return await _callBackend(
    `/tax_returns/${id}/send_reminder`,
    'POST',
    {
      company_id: companyId,
    },
    token,
  );
};

export const classifyTaxReturn = async ({
  id,
  companyId,
  watermarkEnabled,
  pagesBySection,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  pagesBySection: ClassificationPageSectionType;
  watermarkEnabled?: boolean;
  token?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}/classify`,
    'POST',
    {
      company_id: companyId,
      pages_by_section: pagesBySection,
      watermark_enabled: watermarkEnabled,
    },
    token,
  );
};

export const resendTaxReturn = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  token?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}/resend`,
    'POST',
    {
      company_id: companyId,
    },
    token,
  );
};

export const getTaxReturnActivityLogs = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  token?: string;
}>): APIResponsePromise<Array<Activity>> => {
  return await _callBackend(
    `/tax_returns/${id}/get_activity_logs?company_id=${companyId}`,
    'GET',
    null,
    token,
  );
};

export const sendTaxReturnToDocuments = async ({
  id,
  companyId,
  document_name,
  path,
  save_message_for_future_use,
  save_subject_for_future_use,
  subject,
  message,
  watermark_enabled,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  document_name: string;
  message: string;
  subject: string;
  path: string;
  save_message_for_future_use: boolean;
  save_subject_for_future_use: boolean;
  watermark_enabled?: boolean;
  token?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}/send_to_documents`,
    'POST',
    {
      company_id: companyId,
      document_name,
      save_message_for_future_use,
      save_subject_for_future_use,
      subject,
      path,
      message,
      watermark_enabled,
    },
    token,
  );
};

export const downloadTaxReturn = async ({
  id,
  companyId,
  token,
  includeOtherPages = false,
  onlySignaturePages = false,
}: APIRequest<{
  id: number;
  companyId: number;
  token?: string;
  includeOtherPages?: boolean;
  onlySignaturePages?: boolean;
}>): APIResponsePromise<{
  link: string;
}> => {
  return await _callBackend(
    `/tax_returns/${id}/download_tax_return`,
    'POST',
    {
      company_id: companyId,
      include_other_pages: includeOtherPages,
      only_signature_pages: onlySignaturePages,
    },
    token,
  );
};

export const approveTaxReturn = async ({
  id,
  company_id,
  reason,
  token,
}: APIRequest<{
  id: number;
  company_id: number;
  reason: string;
}>): APIResponsePromise<{}> => {
  return await _callBackend(
    `/tax_returns/${id}/approve`,
    'POST',
    {
      reason,
      company_id,
    },
    token,
  );
};

export const requestChanges = async ({
  id,
  company_id,
  reason,
  token,
}: APIRequest<{
  id: number;
  company_id: number;
  reason: string;
}>): APIResponsePromise<{}> => {
  return await _callBackend(
    `/tax_returns/${id}/request_changes`,
    'POST',
    {
      reason,
      company_id,
    },
    token,
  );
};

export const updateInvoicePaidStatus = async ({
  id,
  company_id,
  paid,
  token,
}: APIRequest<{
  id: number;
  company_id: number;
  paid: boolean;
}>): APIResponsePromise<{}> => {
  return await _callBackend(
    `/tax_returns/${id}/update_invoice_paid_status`,
    'POST',
    {
      company_id,
      paid,
    },
    token,
  );
};

export const getDeliveryMessageTemplates = async ({
  token,
}: APIRequest): APIResponsePromise<Array<DeliveryMessageTemplate>> =>
  await _callBackend(
    '/tax_return_message_templates/get_delivery_message_templates',
    'GET',
    null,
    token,
  );

export const updateDeliveryMessageTemplate = async ({
  id,
  token,
  ...data
}: APIRequest<{
  id: number;
}>): APIResponsePromise<{}> => {
  return await _callBackend(
    `/tax_return_message_templates/${id}`,
    'PATCH',
    {
      ...data,
    },
    token,
  );
};

export const createDeliveryMessageTemplate = async ({
  token,
  ...data
}: APIRequest<{
  name: string;
  subject: string;
  body: string;
  reminder_subject: string;
  reminder_message: string;
}>): APIResponsePromise<{}> => {
  return await _callBackend(
    '/tax_return_message_templates',
    'POST',
    {
      ...data,
    },
    token,
  );
};

export const getTaxReturnById = async ({
  id,
  token,
  company_id,
}: APIRequest<{
  id: number;
  company_id: number;
}>): APIResponsePromise<TaxReturn> => {
  return await _callBackend(
    `/tax_returns/${id}?company_id=${company_id}`,
    'GET',
    {},
    token,
  );
};

export const getTaxReturnOriginalImages = async ({
  id,
  company_id,
  token,
}: APIRequest<{
  id: number;
  company_id: number;
}>): APIResponsePromise<Array<{ page: number; url: string }>> => {
  return await _callBackend(
    `/tax_returns/${id}/get_original_images?company_id=${company_id}`,
    'GET',
    {},
    token,
  );
};

// TODO @chris empty mock call as we should have this as a POST endpoint, using GET is a security risk
export const autoClassifyTaxReturn = async ({
  id,
  token,
}: APIRequest<{
  id: number;
}>): APIResponsePromise<
  Array<{ form_name: string; form_type: string; page: string }>
> => {
  return await _callBackend(
    `/tax_returns/${id}/get_suggested_form_types`,
    'GET',
    {},
    token,
  );
};

// Get upload link for K-1 package
export const getUploadLinkForK1Package = async ({
  companyId,
  token,
}: {
  companyId: number;
  token: string;
}): Promise<{ upload_link: string; id: string }> => {
  return await _callBackend(
    `/tax_returns/get_upload_link_for_k1_package?company_id=${companyId}`,
    'GET',
    null,
    token,
  );
};

// Process K-1 package after upload
export const processK1Package = async ({
  fileId,
  companyId,
  taxReturnId,
  documentName,
  token,
}: {
  fileId: string;
  companyId: number;
  taxReturnId: number;
  documentName: string;
  token: string;
}): Promise<any> => {
  return await _callBackend(
    `/tax_returns/${taxReturnId}/process_k1_package`,
    'POST',
    {
      file_id: fileId,
      company_id: companyId,
      document_name: documentName,
    },
    token,
  );
};

// Get K-1 package download link
export const getK1PackageDownloadLink = async ({
  documentPath,
  token,
}: {
  documentPath: string;
  token: string;
}): Promise<{ link: string }> => {
  return await _callBackend(
    '/tax_returns/get_k1_package_download_link',
    'GET',
    {
      document_path: documentPath,
    },
    token,
  );
};

// Resend K-1 package email
export const resendK1PackageEmail = async ({
  token,
}: {
  token: string;
}): Promise<{ message: string }> => {
  return await _callBackend(
    '/tax_returns/resend_k1_package_email',
    'POST',
    {
      token,
    },
    token,
  );
};

export const sendK1PackageToPartner = async ({
  company_id,
  tax_return_id,
  document_path,
  partner_email,
  partner_name,
  token,
}: APIRequest<{
  company_id: number;
  tax_return_id: number;
  document_path: string;
  partner_email: string;
  partner_name: string;
  token: string;
}>): APIResponsePromise<{}> => {
  return await _callBackend(
    `/tax_returns/${tax_return_id}/send_k1_package_to_partner`,
    'POST',
    {
      company_id,
      document_path,
      partner_email,
      partner_name,
    },
    token,
  );
};

export const createTaxReturnCheckoutSession = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  token: string;
}>): APIResponsePromise<{ checkout_url: string }> => {
  return await _callBackend(
    `/tax_returns/${id}/create_checkout_session`,
    'POST',
    {
      company_id: companyId,
    },
    token,
  );
};

export const updateReminderSettings = async ({
  id,
  companyId,
  reminderEmailSubject,
  reminderEmailMessage,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  reminderEmailSubject: string;
  reminderEmailMessage: string;
  token?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    `/tax_returns/${id}/update_reminder_settings`,
    'POST',
    {
      company_id: companyId,
      reminder_email_subject: reminderEmailSubject,
      reminder_email_message: reminderEmailMessage,
    },
    token,
  );
};
